import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText,
    Button, Alert, CircularProgress, Box, Typography, Grid
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { ReservationService } from '../api/services/reservationService';
import { FinanceService } from '../api/services/financeService';

export default function CheckInReservationModal({ open, onClose, reservation, tenantId, onSuccess, setNotification }) {
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (open) {
            setError(null);
        }
    }, [open]);

    const handleConfirm = async () => {
        setIsProcessing(true);
        setError(null);
        try {
            await ReservationService.checkIn(reservation.id, tenantId);


            if (setNotification) {
                setNotification({ open: true, message: 'Guest successfully checked in. Status updated to In-house.', severity: 'success' });
            }
            if (onSuccess) onSuccess();
            onClose();
        } catch (err) {
            const msg = err?.response?.data?.message || err?.message || 'Failed to check-in reservation';
            if (setNotification) {
                setNotification({ open: true, message: msg, severity: 'error' });
            } else {
                setError(msg);
            }
        } finally {
            setIsProcessing(false);
        }
    };

    if (!reservation) return null;

    return (
        <Dialog
            open={open}
            onClose={isProcessing ? undefined : onClose}
            maxWidth="xs"
            fullWidth
            PaperProps={{ sx: { borderRadius: 3, padding: 1 } }}
        >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 'bold', color: 'success.main' }}>
                <CheckCircleIcon /> Check In Guest
            </DialogTitle>
            <DialogContent dividers>
                {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}

                {['pre-checked-in', 'pre-checked in'].includes(String(reservation.checkInStatus || '').toLowerCase()) && (
                    <Box sx={{ mb: 3, p: 2, bgcolor: '#ebf8ff', borderRadius: 2, border: '1px solid #90cdf4' }}>
                        <Typography variant="subtitle2" color="primary" fontWeight="bold" gutterBottom>
                            Web Check-In Complete
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                            The guest has completed their pre-arrival registration online. Please verify their provided details and ID before handing over the keys.
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <Typography variant="caption" color="text.secondary">ID Proof Number</Typography>
                                <Typography variant="body2" fontWeight="bold">{reservation.guest?.idProof || 'N/A'}</Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="caption" color="text.secondary">Document</Typography>
                                <Box>
                                    {reservation.guest?.idDocumentUrl ? (
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            color="primary"
                                            href={reservation.guest.idDocumentUrl}
                                            target="_blank"
                                            sx={{ mt: 0.5, borderRadius: 1 }}
                                        >
                                            View ID Document
                                        </Button>
                                    ) : (
                                        <Typography variant="body2">Not Uploaded</Typography>
                                    )}
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>
                )}

                <DialogContentText sx={{ color: 'text.primary' }}>
                    Are you sure you want to process the check-in for this reservation? This action will mark the booking as actively In-house, update the associated room status parameters, and lock the guest profile to active status.
                </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ padding: '16px 24px' }}>
                <Button onClick={onClose} variant="outlined" sx={{ borderRadius: 2 }} disabled={isProcessing}>
                    Cancel
                </Button>
                <Button
                    onClick={handleConfirm}
                    variant="contained"
                    color="primary"
                    sx={{ borderRadius: 2 }}
                    disabled={isProcessing}
                >
                    {isProcessing ? <CircularProgress size={20} color="inherit" /> : 'Confirm Check-In'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
